// CreateCollectionDialog.js
import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent } from "@/components/ui/dialog";
import {
  ListBulletIcon,
  VideoIcon,
  UploadIcon,
  MagicWandIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import YouTubeImportDialog from "@/components/Collections/YouTubeImportDialog";
import DocumentImportDialog from "@/components/Collections/DocumentImportDialog";
import AIGenerationDialog from "@/components/Collections/AIGenerationDialog";

const CreateCollectionDialog = ({ open, onClose, onCollectionCreated }) => {
  const navigate = useNavigate();
  const [isYouTubeImportOpen, setIsYouTubeImportOpen] = useState(false);
  const [isDocumentImportOpen, setIsDocumentImportOpen] = useState(false);
  const [isAIGenerationOpen, setIsAIGenerationOpen] = useState(false);

  const handleManualCollection = () => {
    navigate("/collections/new");
    onClose();
  };

  const openYouTubeImportDialog = () => {
    setIsYouTubeImportOpen(true);
  };

  const closeYouTubeImportDialog = () => {
    setIsYouTubeImportOpen(false);
  };

  const openDocumentImportDialog = () => {
    setIsDocumentImportOpen(true);
  };

  const closeDocumentImportDialog = () => {
    setIsDocumentImportOpen(false);
  };

  const openAIGenerationDialog = () => {
    setIsAIGenerationOpen(true);
  };

  const closeAIGenerationDialog = () => {
    setIsAIGenerationOpen(false);
  };

  const collectionOptions = [
    {
      key: "manual",
      title: "Normal Collection",
      description: "Manually create a collection.",
      icon: ListBulletIcon,
      onSelect: handleManualCollection,
    },
    {
      key: "youtube",
      title: "Import From YouTube",
      description: "Import subtitles as cards from a YouTube video.",
      icon: VideoIcon,
      onSelect: openYouTubeImportDialog,
    },
    {
      key: "document",
      title: "Import From Document",
      description: "Import txt, rtf, pdf, md, doc or docx files.",
      icon: UploadIcon,
      onSelect: openDocumentImportDialog,
    },
    {
      key: "ai",
      title: "Generate Using AI",
      description: "Write a description using an AI prompt.",
      icon: MagicWandIcon,
      onSelect: openAIGenerationDialog,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-[28px] border border-line bg-surface p-6 text-fg shadow-panel backdrop-blur-2xl">
        <div className="space-y-6">
          <div className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-fg">
              Create a new collection
            </DialogTitle>
            <p className="text-sm text-fg-muted">
              Choose how you want to get started.
            </p>
          </div>
          <div className="space-y-3">
            {collectionOptions.map(({ key, title, description, icon: Icon, onSelect }) => (
              <button
                key={key}
                type="button"
                onClick={onSelect}
                className="group relative flex w-full items-start gap-4 rounded-2xl border border-line bg-surface/80 px-5 py-4 text-left transition-all duration-200 hover:border-primary/40 hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-elevated text-fg transition-colors duration-200 group-hover:border-primary/30 group-hover:text-primary/80">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex flex-1 flex-col gap-1">
                  <span className="text-base font-medium text-fg">
                    {title}
                  </span>
                  <span className="text-sm text-fg-muted">
                    {description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
      <YouTubeImportDialog
        open={isYouTubeImportOpen}
        onClose={closeYouTubeImportDialog}
      />
      <DocumentImportDialog
        open={isDocumentImportOpen}
        onClose={closeDocumentImportDialog}
      />
      <AIGenerationDialog
        open={isAIGenerationOpen}
        onClose={closeAIGenerationDialog}
        onCollectionCreated={onCollectionCreated}
      />
    </Dialog>
  );
};

export default CreateCollectionDialog;

